/**
 * Portal API client.
 *
 * Callers hold a Supabase session in React state, but that state can outlive
 * the access token inside it: Supabase's auto-refresh timer is throttled while
 * a tab is backgrounded, so a tab left open past the token's lifetime sends an
 * expired JWT and the worker answers 401. Registering the auth client here lets
 * every request read a freshly refreshed token instead, and retry once when the
 * worker still rejects it.
 */

interface PortalAuthClient {
  auth: {
    getSession: () => Promise<{data: {session: {access_token: string} | null}}>;
    refreshSession: () => Promise<{data: {session: {access_token: string} | null}}>;
  };
}

let authClient: PortalAuthClient | null = null;

/** Registered once at start-up; tests exercise the token-argument path. */
export function registerPortalAuth(client: PortalAuthClient): void {
  authClient = client;
}

export async function portalApi<T>(path: string, accessToken: string): Promise<T> {
  return portalApiRequest<T>(path, accessToken);
}

export class PortalApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly traceId?: string,
  ) {
    super(message);
    this.name = 'PortalApiError';
  }
}

export async function portalApiRequest<T>(
  path: string,
  accessToken: string,
  init: RequestInit = {},
): Promise<T> {
  return unwrap<T>(await authenticatedResponse(path, accessToken, init));
}

export async function portalArtifact(path: string, accessToken: string, signal?: AbortSignal): Promise<Blob> {
  const response = await authenticatedResponse(path, accessToken, signal ? {signal} : {});
  if (!response.ok) return unwrap<never>(response);
  return response.blob();
}

async function authenticatedResponse(path: string, accessToken: string, init: RequestInit): Promise<Response> {
  const response = await send(path, await freshToken(accessToken), init);
  if (response.status !== 401 || !authClient) return response;

  // A 401 past this point means the token we just read was itself stale, which
  // happens when the tab woke up between reading it and the request landing.
  const refreshed = await authClient.auth.refreshSession().then(
    ({data}) => data.session?.access_token,
    () => undefined,
  );
  if (!refreshed) return response;
  await response.body?.cancel();
  return send(path, refreshed, init);
}

async function send(path: string, accessToken: string, init: RequestInit): Promise<Response> {
  return fetch(path, {
    ...init,
    headers: {authorization: `Bearer ${accessToken}`, ...init.headers},
  });
}

async function unwrap<T>(response: Response): Promise<T> {
  const body = (await response.json().catch(() => null)) as (T & {error?: string; trace_id?: string}) | null;
  if (!response.ok) throw new PortalApiError(body?.error ?? `Request failed (${response.status})`, response.status, body?.trace_id);
  return body as T;
}

/** getSession() transparently refreshes an expired token before returning it. */
async function freshToken(fallback: string): Promise<string> {
  if (!authClient) return fallback;
  try {
    const {data} = await authClient.auth.getSession();
    return data.session?.access_token ?? fallback;
  } catch {
    return fallback;
  }
}

/** Credential-backed workspace routes use the signed-in employee in every environment. */
export const workspaceFetch: typeof fetch = (input, init = {}) => {
  const path = String(input);
  if (!path.startsWith('/api/workspace/')) throw new Error('Expected a workspace API path.');
  return authenticatedResponse(path, '', init);
};
